import React, { useState } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Statistic,
  Form,
  InputNumber,
  Button,
  Typography,
  Tag
} from 'antd';
import { StockOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const DollarCostAveragingModal = ({
  visible,
  selectedStock,
  onClose
}) => {
  const [form] = Form.useForm();
  const [additionalPrice, setAdditionalPrice] = useState(0);
  const [additionalQuantity, setAdditionalQuantity] = useState(0);
  const [calculationResult, setCalculationResult] = useState(null);

  // 모달이 열릴 때마다 초기화
  React.useEffect(() => {
    if (visible && selectedStock) {
      const currentPrice = selectedStock.current_price || 0;
      setAdditionalPrice(currentPrice);
      setAdditionalQuantity(0);
      setCalculationResult(null);
      form.resetFields();
      form.setFieldsValue({
        additionalPrice: currentPrice,
        additionalQuantity: 0
      });
    }
  }, [visible, selectedStock, form]);

  // 추가 매수 입력 변경 핸들러
  const handleAdditionalInputChange = (field, value) => {
    if (field === 'price') {
      setAdditionalPrice(value || 0);
    } else if (field === 'quantity') {
      setAdditionalQuantity(value || 0);
    }
  };

  // 물타기 계산 함수
  const calculateDollarCostAveraging = () => {
    if (!selectedStock || !additionalPrice || !additionalQuantity) {
      return;
    }

    const currentHolding = {
      quantity: selectedStock.quantity,
      avgPrice: selectedStock.avg_buy_price,
      totalAmount: selectedStock.avg_buy_price * selectedStock.quantity,
      currentPrice: selectedStock.current_price,
      evaluationAmount: selectedStock.evaluation_amount,
      profitLoss: selectedStock.profit_loss,
      profitLossPercent: selectedStock.profit_loss_percent
    };

    const additionalPurchase = {
      quantity: additionalQuantity,
      price: additionalPrice,
      amount: additionalPrice * additionalQuantity
    };

    const afterPurchase = {
      quantity: currentHolding.quantity + additionalPurchase.quantity,
      totalInvestment: currentHolding.totalAmount + additionalPurchase.amount,
      avgPrice: 0,
      evaluationAmount: 0,
      profitLoss: 0,
      profitLossPercent: 0
    };

    afterPurchase.avgPrice = afterPurchase.totalInvestment / afterPurchase.quantity;
    afterPurchase.evaluationAmount = afterPurchase.quantity * selectedStock.current_price;
    afterPurchase.profitLoss = afterPurchase.evaluationAmount - afterPurchase.totalInvestment;
    afterPurchase.profitLossPercent = (afterPurchase.profitLoss / afterPurchase.totalInvestment) * 100;

    setCalculationResult({
      before: currentHolding,
      additional: additionalPurchase,
      after: afterPurchase
    });
  };

  // 모달 닫기 핸들러
  const handleModalClose = () => {
    setAdditionalPrice(0);
    setAdditionalQuantity(0);
    setCalculationResult(null);
    form.resetFields();
    onClose();
  };

  if (!selectedStock) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StockOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <span>물타기 계산기</span>
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {selectedStock.stock_name} ({selectedStock.symbol})
          </Tag>
        </div>
      }
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {/* 보유 정보 섹션 */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ marginBottom: '16px' }}>
          📊 보유 정보
        </Title>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic
              title="평균 단가"
              value={Math.round(selectedStock.avg_buy_price)}
              precision={0}
              suffix="₩"
              valueStyle={{ fontSize: '16px' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="보유 수량"
              value={selectedStock.quantity}
              precision={0}
              suffix="주"
              valueStyle={{ fontSize: '16px' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="보유 금액"
              value={Math.round(selectedStock.avg_buy_price * selectedStock.quantity)}
              precision={0}
              suffix="₩"
              valueStyle={{ fontSize: '16px' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 추가 매수 정보 섹션 */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ marginBottom: '16px' }}>
          💰 추가 매수 정보
        </Title>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="추가 매수 가격"
                name="additionalPrice"
                rules={[{ required: true, message: '매수 가격을 입력하세요' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  formatter={(value) => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/₩\s?|(,*)/g, '')}
                  placeholder="매수 가격"
                  value={additionalPrice}
                  onChange={(value) => handleAdditionalInputChange('price', value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="추가 매수 수량"
                name="additionalQuantity"
                rules={[{ required: true, message: '매수 수량을 입력하세요' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  placeholder="매수 수량"
                  suffix="주"
                  value={additionalQuantity}
                  onChange={(value) => handleAdditionalInputChange('quantity', value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="추가 매수 금액">
                <InputNumber
                  style={{ width: '100%' }}
                  value={additionalPrice * additionalQuantity}
                  precision={0}
                  formatter={(value) => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  disabled
                  suffix="₩"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="center" style={{ marginTop: '16px' }}>
            <Button
              type="primary"
              size="large"
              onClick={calculateDollarCostAveraging}
              disabled={!additionalPrice || !additionalQuantity}
            >
              계산하기
            </Button>
          </Row>
        </Form>
      </Card>

      {/* 계산 결과 섹션 */}
      {calculationResult && (
        <Card size="small">
          <Title level={5} style={{ marginBottom: '16px' }}>
            📈 추가 매수 전후 비교
          </Title>
          
          <Row gutter={[16, 16]}>
            {/* 보유 평단가 비교 */}
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: '24px' }}>
                <Text strong>보유 평단가</Text>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'end', height: '200px' }}>
                  <div style={{ textAlign: 'center', marginRight: '20px' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max((calculationResult.before.avgPrice / Math.max(calculationResult.before.avgPrice, calculationResult.after.avgPrice)) * 150, 20)}px`,
                        backgroundColor: '#ff7875',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {(calculationResult.before.avgPrice / 1000).toFixed(0)}K
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 전</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      ₩{calculationResult.before.avgPrice.toLocaleString()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max((calculationResult.after.avgPrice / Math.max(calculationResult.before.avgPrice, calculationResult.after.avgPrice)) * 150, 20)}px`,
                        backgroundColor: '#52c41a',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {(calculationResult.after.avgPrice / 1000).toFixed(0)}K
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 후</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      ₩{calculationResult.after.avgPrice.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <Text style={{ 
                    color: calculationResult.after.avgPrice < calculationResult.before.avgPrice ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold'
                  }}>
                    {((calculationResult.after.avgPrice - calculationResult.before.avgPrice) / calculationResult.before.avgPrice * 100).toFixed(2)}%
                  </Text>
                </div>
              </div>
            </Col>

            {/* 보유 수량 비교 */}
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: '24px' }}>
                <Text strong>보유 수량</Text>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'end', height: '200px' }}>
                  <div style={{ textAlign: 'center', marginRight: '20px' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max((calculationResult.before.quantity / calculationResult.after.quantity) * 150, 20)}px`,
                        backgroundColor: '#1890ff',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {calculationResult.before.quantity}
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 전</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      {calculationResult.before.quantity.toLocaleString()}주
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '60px',
                        height: '150px',
                        backgroundColor: '#52c41a',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {calculationResult.after.quantity}
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 후</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      {calculationResult.after.quantity.toLocaleString()}주
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    +{(calculationResult.after.quantity - calculationResult.before.quantity).toLocaleString()}주
                  </Text>
                </div>
              </div>
            </Col>

            {/* 보유 금액 비교 */}
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: '24px' }}>
                <Text strong>보유 금액</Text>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'end', height: '200px' }}>
                  <div style={{ textAlign: 'center', marginRight: '20px' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max((calculationResult.before.totalAmount / calculationResult.after.totalInvestment) * 150, 20)}px`,
                        backgroundColor: '#722ed1',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}
                    >
                      {(calculationResult.before.totalAmount / 1000000).toFixed(1)}M
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 전</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      ₩{(calculationResult.before.totalAmount / 1000000).toFixed(1)}M
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '60px',
                        height: '150px',
                        backgroundColor: '#52c41a',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}
                    >
                      {(calculationResult.after.totalInvestment / 1000000).toFixed(1)}M
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 후</Text>
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      ₩{(calculationResult.after.totalInvestment / 1000000).toFixed(1)}M
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    +₩{((calculationResult.after.totalInvestment - calculationResult.before.totalAmount) / 1000000).toFixed(1)}M
                  </Text>
                </div>
              </div>
            </Col>

            {/* 수익률 비교 */}
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: '24px' }}>
                <Text strong>수익률</Text>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'end', height: '200px' }}>
                  <div style={{ textAlign: 'center', marginRight: '20px' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max(Math.abs(calculationResult.before.profitLossPercent) * 3, 20)}px`,
                        backgroundColor: calculationResult.before.profitLossPercent >= 0 ? '#52c41a' : '#ff4d4f',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {calculationResult.before.profitLossPercent.toFixed(1)}%
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 전</Text>
                    <div style={{ fontSize: '10px', color: calculationResult.before.profitLossPercent >= 0 ? '#52c41a' : '#ff4d4f' }}>
                      {calculationResult.before.profitLossPercent.toFixed(2)}%
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '60px',
                        height: `${Math.max(Math.abs(calculationResult.after.profitLossPercent) * 3, 20)}px`,
                        backgroundColor: calculationResult.after.profitLossPercent >= 0 ? '#52c41a' : '#ff4d4f',
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {calculationResult.after.profitLossPercent.toFixed(1)}%
                    </div>
                    <Text style={{ fontSize: '12px' }}>매수 후</Text>
                    <div style={{ fontSize: '10px', color: calculationResult.after.profitLossPercent >= 0 ? '#52c41a' : '#ff4d4f' }}>
                      {calculationResult.after.profitLossPercent.toFixed(2)}%
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <Text style={{ 
                    color: (calculationResult.after.profitLossPercent - calculationResult.before.profitLossPercent) >= 0 ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold'
                  }}>
                    {((calculationResult.after.profitLossPercent - calculationResult.before.profitLossPercent) >= 0 ? '+' : '')}
                    {(calculationResult.after.profitLossPercent - calculationResult.before.profitLossPercent).toFixed(2)}%p
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </Modal>
  );
};

export default DollarCostAveragingModal;
